import {Box, Typography} from '@mui/material';
import {ReactComponent as PrintIcon} from '../../../assets/images/print-base-icon.svg';
import {ReactComponent as DownloadIcon} from '../../../assets/images/download-base-icon.svg';
import {ReactComponent as RemarkIcon} from '../../../assets/images/media-remark-icon.svg';
import {ReactComponent as PicInPicIcon} from '../../../assets/images/picture-in-picture-icon.svg';
import ArrIconButton from '../../common/arr-icon-button/ArrIconButton';
import DocPagination from './doc-pagination/DocPagination';
import './DocNavigation.scss';
import {t} from 'i18next';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '../../../redux/store';
import {setActiveDocument, storeDocIdInHistory} from '../docViewerUtils';
import React, {FC, useCallback, useEffect, useRef, useState} from 'react';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ArrMenu, {
  ArrMenuHandle,
  CustomMenuItem,
} from '../../common/arr-menu/ArrMenu';
import {useGetDocDownloadFormats} from '../../../hooks/document/useDocument';
import {MiniDocListResponse} from '../../../shared/interfaces';
import {IDocViewDocListRowData} from '../doc-list/interfaces';
import {prepareMiniDocDataRequest} from '../doc-list/DocViewDocListDataGrid';
import {
  getDownloadDocument,
  postPrintDocument,
  getMiniDocListGridData,
  getDownloadProductionPDF,
} from '../../../api/endpoints/documentApi';
import {
  IGetDownloadDocumentParams,
  IPrintDocumentRequest,
  TranslationItem,
} from '../../../api/types/document.type';
import ArrToastNotification from '../../common/arr-toast-notification/ArrToastNotification';
import {ToastType} from '../../../shared/constants';
import {useMediaViewer} from '../panels/document-center-panel/DocumentViewerPanel/useMediaViewer';
import {setDocMediaReviewerRemarksPanel} from '../../../redux/slice/docViewSlice';
import ArrTooltip from '../../common/arr-tooltip/ArrTooltip';

type DocNavigationProps = {
  isResponsiveMode: boolean;
  isMedia?: boolean;
  isShowDownload?: boolean;
  isShowPrint?: boolean;
  isTranslationTab?: boolean;
  isImageTab?: boolean;
  disabledDownload?: boolean;
  selectedTranslation?: TranslationItem;
  codingFormUpdated?: boolean;
};
export type DownloadFormatType = {
  name: string;
  seqNo: string;
};

const DocNavigation: FC<DocNavigationProps> = ({
  isResponsiveMode,
  isMedia,
  isShowDownload = true,
  isShowPrint = true,
  isTranslationTab = false,
  isImageTab = false,
  disabledDownload = false,
  selectedTranslation,
  codingFormUpdated = false,
}) => {
  const menuRef = useRef<ArrMenuHandle | null>(null);
  const downloadButtonRef = useRef<HTMLButtonElement | null>(null);
  const downloadButtonMoreMenuRef = useRef<HTMLButtonElement | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [downloadMenuOpen, setDownloadMenuOpen] = useState(false);

  const activeDoc = useSelector((state: RootState) => state.docView.activeDoc);

  const {showMediaReviewerRemarksPanel, showMediaReviewerRemarks} =
    useMediaViewer();
  const dispatch = useDispatch();

  const docListMasterId = useSelector(
    (state: RootState) => state.docView.documentListMasterId
  );
  const totalDocsCount = useSelector(
    (state: RootState) => state.docView.totalDocsCount
  );
  const docViewConfiguration = useSelector(
    (state: RootState) => state.docView.docViewConfiguration
  );
  const sourceTypeCode = useSelector(
    (state: RootState) => state.docView.sourceTypeCode
  );
  const [showPrintToast, setShowPrintToast] = useState<boolean>(false);

  const {data: docDownloadFormats = [], refetch: getDocDownloadFormats} =
    useGetDocDownloadFormats(
      `formats?docId=${activeDoc?.documentId}&type=DOWNLOAD`
    );

  let actionMenuItems: CustomMenuItem[] = [];

  const printDocument = async (payload: IPrintDocumentRequest) => {
    try {
      const {status} = await postPrintDocument(payload);

      if (status === 200) {
        setShowPrintToast(true);
        setTimeout(() => {
          setShowPrintToast(false);
        }, 5000);
      }
    } catch (error) {
      console.error('Print Failed!', error);
    }
  };

  const actionDownloadOptions: CustomMenuItem[] = docDownloadFormats.map(
    (item: DownloadFormatType) => {
      const isRedactedNative = item.name.toLocaleLowerCase() === 'rnfv';
      return {
        id: item.name,
        label: isRedactedNative
          ? `${t('docView.headers.mainPanelTabs.rnfv')} ${item.seqNo}`
          : `${item.name.toLocaleLowerCase() === 'media' ? 'MP3' : item.name} ${item.seqNo}`,
        value: item.seqNo,
      } as CustomMenuItem;
    }
  );

  if (isResponsiveMode) {
    if (isMedia) {
      if (showMediaReviewerRemarks) {
        actionMenuItems.push({
          id: 'remarks',
          label: t('docView.remarks'),
        });
      }
      actionMenuItems.push({
        id: 'search_hit',
        label: t('docView.searchHit'),
      });
    } else if (!isImageTab) {
      actionMenuItems.push({
        id: 'print',
        label: t('docView.print'),
      });
    }

    if (docViewConfiguration.isAllowNativeDownloads) {
      actionMenuItems.push({
        id: 'download',
        label: t('docView.download'),
        submenu: actionDownloadOptions,
      });
    }
  } else {
    actionMenuItems = actionDownloadOptions;
  }

  useEffect(() => {
    if (activeDoc) getDocDownloadFormats();
  }, [activeDoc, getDocDownloadFormats]);

  const menuButtonClickHandler = (e: React.MouseEvent<HTMLElement>) => {
    setMenuOpen(true);
    setDownloadMenuOpen(true);
    menuRef.current?.open(e.currentTarget as HTMLElement);
  };

  const menuItemClickHandler = (item: CustomMenuItem) => {
    if (item.id === 'remarks') {
      handleReviewerRemarks();
    } else if (item.id === 'print') {
      handleDocumentPrint();
    } else {
      handleDownloadSelection(item);
    }
  };

  const handleMenuClose = () => {
    setMenuOpen(false);
    setDownloadMenuOpen(false);

    requestAnimationFrame(() => {
      if (isResponsiveMode && isShowDownload) {
        downloadButtonMoreMenuRef.current?.focus();
      } else {
        downloadButtonRef.current?.focus();
      }
    });
  };

  const handleReviewerRemarks = useCallback(() => {
    dispatch(setDocMediaReviewerRemarksPanel(!showMediaReviewerRemarksPanel));
  }, [dispatch, showMediaReviewerRemarksPanel]);

  const handleDownloadSelection = async (item: CustomMenuItem) => {
    if (!activeDoc) return;
    const sequenceNumber = item.value ?? '1';
    const downloadParams: IGetDownloadDocumentParams = {
      DocId: activeDoc.docId,
      Type: item.id, // e.g., PDF, TIFF, TXT
      SeqenceNumber: sequenceNumber, // This can be dynamically set based on the document
      DisplayDocumentId: activeDoc.documentId,
    };

    try {
      // Fetch the document using the API function
      const response = await getDownloadDocument(downloadParams);

      const {blob, filename} = response;

      // Create a temporary URL for the Blob data
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename; // Use the filename from the API response
      a.click(); // Trigger the download
      window.URL.revokeObjectURL(url); // Clean up the URL object
    } catch (error) {
      console.error('Download failed', error);
      // Optionally, display a toast/alert for the user about the error
    }
  };

  const handleImageDownload = async () => {
    if (!activeDoc || !selectedTranslation) return;
    const downloadParams: IGetDownloadDocumentParams = {
      DocId: activeDoc.documentId,
      Type: 'TIFF',
      SeqenceNumber: selectedTranslation?.seqNo ?? '1',
      ContextType: selectedTranslation?.typeCode ?? '',
      ContextId: selectedTranslation?.contextID ?? '',
      DisplayDocumentId: '',
    };

    try {
      const response = await getDownloadProductionPDF(downloadParams);

      const {blob, filename} = response;

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed', error);
    }
  };
  const fetchRowData = async (
    start: number,
    pageSize: number
  ): Promise<IDocViewDocListRowData[]> => {
    const payload = prepareMiniDocDataRequest(
      start,
      pageSize - 1,
      docListMasterId,
      docViewConfiguration,
      sourceTypeCode
    );
    let response: MiniDocListResponse;
    let rowsData: IDocViewDocListRowData[] = [];

    try {
      response = (await getMiniDocListGridData(payload)) as MiniDocListResponse;
      rowsData = JSON.parse(response.serverData) as IDocViewDocListRowData[];
    } catch (error) {
      console.error(error);
    }

    rowsData =
      rowsData?.map((row) => {
        return {
          ...row,
          IsChecked: false,
          IsCodeSame: false,
          IsSelected: false,
          IsCompleted: Boolean(Number(row.IsCompleted)),
        };
      }) ?? [];

    return rowsData;
  };
  const setNavigatedDoc = async (rowNumber: number) => {
    const rows = await fetchRowData(rowNumber, 1);
    if (rows.length) {
      const row = rows[0];
      const activeDocument = {
        docId: row.DocID,
        documentId: row.MiniDocListDocID,
        fileName: row.FileName,
        rowNumber: rowNumber,
        isCompleted: row.IsCompleted,
        fileTypeCode: row.FileTypeCode,
        miniMediaLength: row.MiniMediaLength,
        miniAudioTrimmedLength: row.MiniAudioTrimmedLength,
        miniAudioDocDateTimeOnly: row.MiniAudioDocDateTimeOnly,
        docExt: row.MiniDocListDocExt as string,
        documentGUID: row.DocumentGUID,
        ingestionGUID: row.IngestionGUID,
        __InternalExcelProtectedSheets: row.__InternalExcelProtectedSheets,
        __InternalExcelProtectedWorkbook: row.__InternalExcelProtectedWorkbook,
        __InternalHiddenProperties: row.__InternalHiddenProperties,
        __InternalHiddenPropertiesText: row.__InternalHiddenPropertiesText,
        internalVideoPlayerReady: row.__InternalVideoPlayerReady,
        ActualParameters: row?.ActualParameters ?? '',
        ConceptParameters: row?.ConceptParameters ?? '',
        MinConceptScore: row?.MinConceptScore,
      };
      setActiveDocument(activeDocument);
      storeDocIdInHistory(activeDocument);
    }
  };

  const {miniDocListView} = useSelector(
    (state: RootState) => state.docView.docViewConfiguration
  );

  useEffect(() => {
    if (!miniDocListView && !activeDoc) {
      setNavigatedDoc(1);
    }
  }, [miniDocListView, activeDoc]);

  const RenderMediaActions = useCallback(() => {
    return (
      <>
        {showMediaReviewerRemarks && (
          <ArrIconButton
            data-testid="doc-nav-remark-button"
            testId="doc-nav-remark-button"
            className="action-icon"
            icon={<RemarkIcon />}
            ariaLabel={t('docView.remarks')}
            shape="none"
            size="small"
            onClick={handleReviewerRemarks}
            tooltipContent={t('docView.remarks')}
            tooltipFocusableWrapper
          />
        )}
        <ArrIconButton
          data-testid="doc-nav-print-button"
          testId="doc-nav-print-button"
          className="action-icon"
          icon={<PicInPicIcon />}
          ariaLabel={t('docView.searchHit')}
          shape="none"
          size="small"
          tooltipContent={t('docView.searchHit')}
          tooltipFocusableWrapper
        />
      </>
    );
  }, [handleReviewerRemarks, showMediaReviewerRemarks]);

  const handleDocumentPrint = () => {
    // Get Print Document
    if (activeDoc) {
      const {documentId, docId, docExt = ''} = activeDoc;
      const payload: IPrintDocumentRequest = {
        documentId: parseInt(documentId),
        documentName: docId,
        docExt: docExt as string,
        isPreview: false,
      };
      printDocument(payload);
    }
  };

  return (
    <Box className="doc-navigation">
      <Box className="selected-doc-panel" tabIndex={0}>
        {activeDoc?.docId && (
          <>
            <Typography
              component="span"
              className="selected-doc typo-text subhead5_size"
              data-testid="document-id"
            >
              {t('docView.docId')}:
            </Typography>
            <Typography
              component="span"
              className="selected-doc typo-text subhead5_size"
              data-testid={`doc-navigation-active-doc-id`}
            >
              {` ${activeDoc.docId}`}
            </Typography>
          </>
        )}
      </Box>
      <Box>
        <DocPagination
          entityText={t('docView.docs')}
          onSelectionChange={(rowNumber) => {
            setNavigatedDoc(rowNumber);
          }}
          defaultSelectedNumber={Number(activeDoc?.rowNumber ?? 0)}
          totalCount={totalDocsCount}
          codingFormUpdated={codingFormUpdated}
        />
      </Box>
      <>
        <ArrMenu
          data-testid="doc-nav-menu"
          id="doc-nav-menu"
          className="doc-navigation-action-menu"
          ref={menuRef}
          items={actionMenuItems}
          direction="bottom-right"
          subMenuDirection="left"
          onItemClick={menuItemClickHandler}
          onClose={handleMenuClose}
        />
        {activeDoc?.docId ? (
          <>
            {isResponsiveMode &&
            !isTranslationTab &&
            !isImageTab &&
            isShowDownload ? (
              <Box className="action-panel">
                <ArrIconButton
                  ref={downloadButtonMoreMenuRef}
                  data-testid="doc-nav-menu-button"
                  testId="doc-nav-menu-button"
                  className={`menu-icon ${menuOpen ? 'active' : ''}`}
                  onClick={menuButtonClickHandler}
                  size="medium"
                  shape="none"
                  icon={<MoreVertIcon />}
                  ariaLabel={t('docView.menuIcon')}
                  tooltipFocusableWrapper
                  tooltipContent={t('docView.menu')}
                />
              </Box>
            ) : (
              <Box className="action-panel">
                {isMedia ? (
                  <RenderMediaActions />
                ) : (
                  !isTranslationTab &&
                  !isImageTab &&
                  isShowPrint && (
                    <ArrIconButton
                      data-testid="doc-nav-print-button"
                      testId="doc-nav-print-button"
                      className="action-icon"
                      icon={<PrintIcon />}
                      ariaLabel={t('docView.print')}
                      shape="none"
                      size="small"
                      onClick={handleDocumentPrint}
                      tooltipFocusableWrapper
                      tooltipContent={t('docView.print')}
                    />
                  )
                )}
                {(!isTranslationTab &&
                  isShowDownload &&
                  !isImageTab &&
                  docViewConfiguration.isAllowNativeDownloads && (
                    <ArrIconButton
                      ref={downloadButtonRef}
                      data-testid="doc-nav-download-button"
                      testId="doc-nav-download-button"
                      className={`action-icon ${downloadMenuOpen ? 'active' : ''}`}
                      icon={<DownloadIcon />}
                      ariaLabel={t('docView.download')}
                      shape="none"
                      size="small"
                      onClick={menuButtonClickHandler}
                      disabled={disabledDownload}
                      tooltipFocusableWrapper
                      tooltipContent={t('docView.download')}
                    />
                  )) ||
                  (isImageTab && (
                    <ArrIconButton
                      data-testid="doc-nav-download-button"
                      testId="image-nav-download-button"
                      className={`action-icon ${downloadMenuOpen ? 'active' : ''}`}
                      icon={<DownloadIcon />}
                      ariaLabel={t('docView.download')}
                      shape="none"
                      size="small"
                      onClick={handleImageDownload}
                      disabled={disabledDownload}
                      tooltipFocusableWrapper
                      tooltipContent={t('docView.download')}
                    />
                  ))}
              </Box>
            )}
          </>
        ) : (
          <Box />
        )}
      </>
      {showPrintToast && (
        <ArrToastNotification
          data-testid="print-success-toast"
          title={t('common.success')}
          message={t('docView.printSuccessMessage')}
          status={[ToastType.Success]}
          onClose={() => setShowPrintToast(false)}
        />
      )}
    </Box>
  );
};

export default DocNavigation;
